import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import HttpApi from 'i18next-http-backend';
import { LANGUAGE_BY_COUNTRY, LANGUAGES_BY_COUNTRY } from '@tectonics/translate/enums/language-by-country.enum';
import { getCurrentLanguage } from '@tectonics/controller/language/language-service';
import { overwrite } from '@tectonics/translate/overwrite';
import { handleError } from '@tectonics/observability';
import { TranslationLoadError } from '@tectonics/translation-load-error';

export interface Messages {
  readonly [key: string]: string | Messages;
}

export type BundledResources = Record<string, Record<string, Messages>>;

const SUPPORTED_LANGUAGES = ['en', 'es', 'pt', 'ar', 'tr', 'id', 'th', 'ms'];

export const PRELOADED_NAMESPACES = ['volcanes', 'uber-v2', 'shopify', 'navigation'];

// Namespaces added here are bundled with the application and are available on the first paint,
// instead of being fetched from /translate/locales/{{lng}}/{{ns}}.json by i18next-http-backend.
export const bundledResources: BundledResources = {};

let refreshOverwrite: (() => void) | null = null;
const reportedFailures = new Set<string>();
const reportedMissingKeys = new Set<string>();
let failedLoadingReporterRegistered = false;

export function applyCountryOverwrite(country: string): void {
  const patchesByNamespace = overwrite[country];
  const language = i18n.language;

  if (!patchesByNamespace || !language) {
    return;
  }

  Object.keys(patchesByNamespace).forEach((namespace) => {
    const patch = patchesByNamespace[namespace];
    const namespaceIsAvailable = i18n.hasResourceBundle(language, namespace);

    // Patching a namespace that was not loaded yet would make i18next consider it already loaded,
    // so namespaces still being fetched are patched by the 'loaded' listener instead.
    if (namespaceIsAvailable && patch && typeof patch === 'object') {
      i18n.addResourceBundle(language, namespace, patch, true, true);
    }
  });
}

interface MissingKeyOptions {
  readonly defaultValue?: unknown;
}

function reportMissingKey(
  languages: readonly string[],
  namespace: string,
  key: string,
  fallbackValue: unknown,
  updateMissing: boolean,
  options?: MissingKeyOptions
): void {
  // A call site that supplies its own default is probing for an optional message,
  // so the absence of the key is expected and must not be reported as an error.
  if (options && options.defaultValue !== undefined) {
    return;
  }

  const [currentLanguage] = languages;
  // Without a namespace bundle, every key looks missing; report the load failure separately.
  if (currentLanguage && !i18n.hasResourceBundle(currentLanguage, namespace)) {
    return;
  }

  const language = Array.isArray(languages) ? languages.join(', ') : languages;
  const missingKey = `${language}:${namespace}:${key}`;
  if (reportedMissingKeys.has(missingKey)) {
    return;
  }

  reportedMissingKeys.add(missingKey);
  handleError(Error(`Translation key '${namespace}:${key}' not found for language '${language}'`));
}

function reportFailedLoading(language: string, namespace: string, message: string): void {
  // The browser being offline is the buyer's connection, not a broken deploy.
  if (navigator.onLine === false) {
    return;
  }

  const failure = `${language}:${namespace}`;
  if (reportedFailures.has(failure)) {
    return;
  }

  reportedFailures.add(failure);
  handleError(
    new TranslationLoadError(
      `Translation namespace '${namespace}' failed to load for language '${language}': ${message}`
    )
  );
}

export function clearReportedErrors(): void {
  reportedFailures.clear();
  reportedMissingKeys.clear();
}

function keepOverwriteApplied(country: string): void {
  if (refreshOverwrite) {
    i18n.off('languageChanged', refreshOverwrite);
    i18n.off('loaded', refreshOverwrite);
  }

  refreshOverwrite = () => applyCountryOverwrite(country);

  i18n.on('languageChanged', refreshOverwrite);
  i18n.on('loaded', refreshOverwrite);
}

function selectLanguage(country: string): string {
  const defaultLanguage = LANGUAGE_BY_COUNTRY[country];
  if (!defaultLanguage) {
    throw new Error(`No language found for country code: ${country}`);
  }

  const storedLanguage = getCurrentLanguage();
  const supportedForCountry = (LANGUAGES_BY_COUNTRY[country] || []).map(language => language.code);

  return storedLanguage && supportedForCountry.includes(storedLanguage)
    ? storedLanguage
    : defaultLanguage;
}

function browserLanguage(): string {
  const language = (navigator.languages && navigator.languages[0]) || navigator.language || '';
  const code = language.split('-')[0];

  return SUPPORTED_LANGUAGES.includes(code) ? code : 'en';
}

async function initForLanguage(country: string, language: string): Promise<void> {
  keepOverwriteApplied(country);

  if (!failedLoadingReporterRegistered) {
    i18n.on('failedLoading', reportFailedLoading);
    failedLoadingReporterRegistered = true;
  }

  await i18n
    .use(HttpApi)
    .use(initReactI18next)
    .init({
      lng: language,
      ns: PRELOADED_NAMESPACES,
      debug: Boolean(process.env.NODE_ENV !== 'production'),
      supportedLngs: SUPPORTED_LANGUAGES,
      fallbackLng: 'en',
      resources: { ...bundledResources },
      partialBundledLanguages: true,
      interpolation: {
        escapeValue: false,
      },
      saveMissing: true,
      saveMissingTo: 'current',
      missingKeyHandler: reportMissingKey,
      detection: {
        order: ['queryString', 'cookie', 'localStorage', 'sessionStorage', 'navigator', 'htmlTag'],
        caches: ['cookie'],
      },
      backend: {
        loadPath: '/translate/locales/{{lng}}/{{ns}}.json',
        queryStringParams: { v: COMMITHASH },
      },
    });

  applyCountryOverwrite(country);
}

const initI18n = (country: string): Promise<void> => initForLanguage(country, selectLanguage(country));

export const initI18nWithoutPayment = (country: string): Promise<void> =>
  LANGUAGE_BY_COUNTRY[country]
    ? initI18n(country)
    : initForLanguage(country, browserLanguage());

export default initI18n;
